/**
 * Repositorio de escritura de productos — create, update, lifecycle (deactivate/reactivate/bulk).
 *
 * Módulo profundo: unifica normalización de input, validación de unicidad, INSERT/UPDATE SQL,
 * encolado de sync, eventos de auditoría, y web cache patch.
 */
import { isLegacyProductCategoryHint } from '../../shared/domain/legacyCategoryId'
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';
import type { Product } from '../domain/productTypes';
import type { ProductMutationOpts } from '../domain/catalogTypes';
import { CatalogBarcodeDuplicateError, CatalogSkuDuplicateError } from '../domain/catalogErrors';
import {
  normalizeProductCode,
  productCodeUniquenessKey,
  validateBarcode,
  validateSku,
} from '../domain/catalogValidation';
import { resolveInsertIsActive } from '../domain/catalogProductDefaults';
import { useSessionStore } from '../../shell/session/store/useSessionStore'
import { requireSessionOrganizationId } from '../../shell/session/sessionOrganization'
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { assertCategoryAllowsItemType, resolveCategoryDisplayName } from './categoriesRepo';
import { appendProductCatalogEvent, buildProductUpdateSummary } from './productCatalogEventWriter';
import {
  resolveProductLifecycleRowForMutation,
  resolveProductRowForMutation,
  sumProductStockQuantityFromWebCache,
} from './webCatalogMutationResolve';

async function submitProductCommand(
  operation: 'insert' | 'update',
  payload: Record<string, unknown>,
) {
  const { submitProductCommand: submit } = await import('./submitProductCommand');
  await submit(operation, payload);
}

// ─── Input normalization + uniqueness ───────────────────────────────────────

export const normalizeProductInput = (input: {
  name: string;
  price: number;
  sku: string;
  categoryId: string | null;
  barcode?: string | null;
  itemType?: 'physical' | 'service';
  brandId?: string | null;
  internalNotes?: string | null;
  preferredSupplierId?: string | null;
  supplierItemCode?: string | null;
  taxClass?: string | null;
  unitOfMeasure?: Product['unitOfMeasure'];
  sellByWeight?: boolean;
  durationMinutes?: number | null;
  bufferBeforeMinutes?: number | null;
  bufferAfterMinutes?: number | null;
  imageUrl?: string | null;
  /** Reel opcional (bucket product-media); se muestra en el detalle de tienda. */
  videoUrl?: string | null;
  publicDescription?: string | null;
  minOrderQty?: string | number | null;
  maxOrderQty?: string | number | null;
  isPublic?: boolean;
  activeIngredient?: string | null;
  usageTags?: string[] | null;
  allergens?: string[] | null;
  bookingMode?: 'direct' | 'quote_required';
  evaluationProductId?: string | null;
  bookingPaymentMode?: 'inherit' | 'none' | 'deposit' | 'full';
  bookingDepositAmount?: number | null;
  bookingDepositPercent?: number | null;
  kitchenDestination?: 'kitchen' | 'bar' | 'none';
  prepMinutes?: number | null;
  menuAvailable?: boolean;
  visibleOnMenuCarta?: boolean;
  parentProductId?: string | null;
  // M1: When the product has active variants, the parent price is optional —
  // each variant carries its own price. Pass true to skip the >0 price check.
  hasActiveVariants?: boolean;
}) => {
  const name = input.name.trim();
  const price = Number(input.price);
  const itemType: 'physical' | 'service' = input.itemType === 'service' ? 'service' : 'physical';
  let sku = input.sku.trim();
  if (itemType === 'service' && !sku) {
    sku = `SVC-${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`.toUpperCase();
  }

  if (!name) {
    throw new Error('Ingresá un nombre para el producto.');
  }

  if (itemType !== 'service' && !sku) {
    sku = '';
  }

  if (sku) {
    const skuValidationError = validateSku(sku);
    if (skuValidationError) {
      throw new Error(skuValidationError);
    }
  }

  const barcodeRaw = input.barcode != null ? String(input.barcode).trim() : '';
  let barcodeNorm: string | null = null;
  if (barcodeRaw) {
    barcodeNorm = normalizeProductCode(barcodeRaw);
    const barcodeErr = validateBarcode(barcodeNorm);
    if (barcodeErr) {
      throw new Error(barcodeErr);
    }
  }

  // M1: Parent price is optional when the product has active variants.
  if (!input.hasActiveVariants && (Number.isNaN(price) || price <= 0)) {
    throw new Error('Ingresá un precio mayor a cero.');
  }
  // M1: Coerce NaN/negative to 0 when variants carry the real price.
  const normalizedPrice = (input.hasActiveVariants && (Number.isNaN(price) || price < 0)) ? 0 : price;

  const allowedUom = new Set(['unit', 'kg', 'g', 'l', 'ml', 'hour', 'm', 'cm']);
  const unitOfMeasure =
    input.unitOfMeasure != null && allowedUom.has(input.unitOfMeasure) ? input.unitOfMeasure : 'unit';
  const parseOptionalOrderQty = (raw: string | number | null | undefined): number | null => {
    if (raw == null) return null;
    const s = String(raw).trim().replace(',', '.');
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  };
  const minOrderQty = parseOptionalOrderQty(input.minOrderQty);
  const maxOrderQty = parseOptionalOrderQty(input.maxOrderQty);
  if (minOrderQty != null && maxOrderQty != null && minOrderQty > maxOrderQty) {
    throw new Error('La cantidad mínima no puede ser mayor que la máxima.');
  }
  if (unitOfMeasure === 'unit') {
    if (minOrderQty != null && !Number.isInteger(minOrderQty)) {
      throw new Error('La cantidad mínima debe ser un número entero.');
    }
    if (maxOrderQty != null && !Number.isInteger(maxOrderQty)) {
      throw new Error('La cantidad máxima debe ser un número entero.');
    }
  }
  // ponytail: sell_by_weight is derived from UoM (kg/g); no separate product flag.
  const sellByWeight = unitOfMeasure === 'kg' || unitOfMeasure === 'g';
  const prepMinutesRaw = input.prepMinutes != null ? Number(input.prepMinutes) : null;
  if (prepMinutesRaw != null && (!Number.isFinite(prepMinutesRaw) || prepMinutesRaw < 5 || prepMinutesRaw > 240)) {
    throw new Error('Los minutos de preparación deben estar entre 5 y 240.');
  }
  const durationRaw =
    input.durationMinutes != null && Number.isFinite(Number(input.durationMinutes))
      ? Math.floor(Number(input.durationMinutes))
      : null;
  const durationMinutes =
    itemType === 'service' && durationRaw != null && durationRaw > 0 ? durationRaw : null;
  const clampBuffer = (raw: unknown) => {
    if (itemType !== 'service') return 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(180, Math.floor(n));
  };
  const bufferBeforeMinutes = clampBuffer(input.bufferBeforeMinutes);
  const bufferAfterMinutes = clampBuffer(input.bufferAfterMinutes);

  return {
    name,
    sku: normalizeProductCode(sku),
    price: normalizedPrice,
    categoryId: (() => {
      const trimmed =
        input.categoryId != null && String(input.categoryId).trim() !== ''
          ? String(input.categoryId).trim()
          : null;
      return isLegacyProductCategoryHint(trimmed) ? null : trimmed;
    })(),
    barcode: barcodeNorm,
    itemType,
    brandId:
      input.brandId != null && String(input.brandId).trim() !== '' ? String(input.brandId).trim() : null,
    internalNotes:
      input.internalNotes != null && String(input.internalNotes).trim() !== ''
        ? String(input.internalNotes).trim()
        : null,
    preferredSupplierId:
      input.preferredSupplierId != null && String(input.preferredSupplierId).trim() !== ''
        ? String(input.preferredSupplierId).trim()
        : null,
    supplierItemCode:
      input.supplierItemCode != null && String(input.supplierItemCode).trim() !== ''
        ? String(input.supplierItemCode).trim()
        : null,
    taxClass:
      input.taxClass != null && String(input.taxClass).trim() !== ''
        ? String(input.taxClass).trim()
        : null,
    unitOfMeasure,
    sellByWeight,
    durationMinutes,
    bufferBeforeMinutes,
    bufferAfterMinutes,
    imageUrl:
      input.imageUrl != null && String(input.imageUrl).trim() !== ''
        ? String(input.imageUrl).trim()
        : null,
    videoUrl:
      input.videoUrl != null && String(input.videoUrl).trim() !== ''
        ? String(input.videoUrl).trim()
        : null,
    publicDescription:
      input.publicDescription != null && String(input.publicDescription).trim() !== ''
        ? String(input.publicDescription).trim()
        : null,
    minOrderQty,
    maxOrderQty,
    isPublic: input.isPublic !== false,
    activeIngredient:
      input.activeIngredient != null && String(input.activeIngredient).trim() !== ''
        ? String(input.activeIngredient).trim()
        : null,
    usageTags:
      input.usageTags != null && Array.isArray(input.usageTags)
        ? input.usageTags.map((t) => String(t).trim()).filter(Boolean)
        : [],
    allergens:
      input.allergens != null && Array.isArray(input.allergens)
        ? input.allergens.map((t) => String(t).trim()).filter(Boolean)
        : [],
    bookingMode:
      itemType === 'service' && input.bookingMode === 'quote_required'
        ? 'quote_required'
        : 'direct',
    evaluationProductId:
      itemType === 'service' &&
      input.bookingMode === 'quote_required' &&
      input.evaluationProductId != null &&
      String(input.evaluationProductId).trim() !== ''
        ? String(input.evaluationProductId).trim()
        : null,
    bookingPaymentMode:
      itemType === 'service' &&
      (input.bookingPaymentMode === 'none' ||
        input.bookingPaymentMode === 'deposit' ||
        input.bookingPaymentMode === 'full')
        ? input.bookingPaymentMode
        : 'inherit',
    bookingDepositAmount:
      itemType === 'service' &&
      input.bookingPaymentMode === 'deposit' &&
      input.bookingDepositAmount != null &&
      Number.isFinite(Number(input.bookingDepositAmount))
        ? Number(input.bookingDepositAmount)
        : null,
    bookingDepositPercent:
      itemType === 'service' &&
      input.bookingPaymentMode === 'deposit' &&
      input.bookingDepositPercent != null &&
      Number.isFinite(Number(input.bookingDepositPercent))
        ? Number(input.bookingDepositPercent)
        : null,
    kitchenDestination:
      input.kitchenDestination === 'bar' ? 'bar' :
      input.kitchenDestination === 'none' ? 'none' :
      'kitchen',
    prepMinutes:
      input.prepMinutes != null && Number.isFinite(Number(input.prepMinutes))
        ? Math.min(240, Math.max(5, Math.floor(Number(input.prepMinutes))))
        : null,
    menuAvailable: input.menuAvailable !== false,
    visibleOnMenuCarta: input.visibleOnMenuCarta !== false,
    parentProductId:
      input.parentProductId != null && String(input.parentProductId).trim() !== ''
        ? String(input.parentProductId).trim()
        : null,
  };
};

export const assertUniqueSku = async (organizationId: string, sku: string, excludedProductId?: string) => {
  if (!productCodeUniquenessKey(sku)) return;
  const key = productCodeUniquenessKey(sku);
        const dup = (getCatalogRamSnapshotCache()?.products ?? []).find(
          (p) =>
            String(p.organization_id ?? '') === organizationId &&
            productCodeUniquenessKey(String(p.sku ?? '')) === key &&
            String(p.id) !== (excludedProductId ?? ''),
        );
        if (dup) throw new CatalogSkuDuplicateError();
        return;
};

export const assertUniqueBarcode = async (
  organizationId: string,
  barcode: string | null,
  excludedProductId?: string
) => {
  if (!productCodeUniquenessKey(barcode)) return;
  const key = productCodeUniquenessKey(barcode);
        const dup = (getCatalogRamSnapshotCache()?.products ?? []).find(
          (p) =>
            String(p.organization_id ?? '') === organizationId &&
            productCodeUniquenessKey(String(p.barcode ?? '')) === key &&
            String(p.id) !== (excludedProductId ?? ''),
        );
        if (dup) throw new CatalogBarcodeDuplicateError();
        return;
};

// ─── Create ─────────────────────────────────────────────────────────────────

const generateAutoSku = async (organizationId: string): Promise<string> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  // Server generates SKU via generate_next_sku; this local read is dead but kept for compatibility.
  const products = getCatalogRamSnapshotCache()?.products ?? [];
  let maxSeq = 0;
  for (const p of products) {
    if (String(p.organization_id ?? '') !== organizationId) continue;
    const sku = String(p.sku ?? '');
    if (sku.startsWith('PRD-')) {
      const seq = Number(sku.slice(4));
      if (Number.isFinite(seq) && seq > maxSeq) maxSeq = seq;
    }
  }
  const nextSeq = maxSeq + 1;
  return `PRD-${String(nextSeq).padStart(4, '0')}`;
};

export const createProduct = async (
  input: {
    name: string;
    price: number;
    sku: string;
    categoryId: string | null;
    barcode?: string | null;
    itemType?: 'physical' | 'service';
    brandId?: string | null;
    internalNotes?: string | null;
    preferredSupplierId?: string | null;
    supplierItemCode?: string | null;
    isActive?: boolean;
    taxClass?: string | null;
    unitOfMeasure?: Product['unitOfMeasure'];
    sellByWeight?: boolean;
    durationMinutes?: number | null;
    bufferBeforeMinutes?: number | null;
    bufferAfterMinutes?: number | null;
    imageUrl?: string | null;
    videoUrl?: string | null;
    publicDescription?: string | null;
    isPublic?: boolean;
    bookingMode?: 'direct' | 'quote_required';
    evaluationProductId?: string | null;
    bookingPaymentMode?: 'inherit' | 'none' | 'deposit' | 'full';
    bookingDepositAmount?: number | null;
    bookingDepositPercent?: number | null;
    kitchenDestination?: 'kitchen' | 'bar' | 'none';
    menuAvailable?: boolean;
    visibleOnMenuCarta?: boolean;
    parentProductId?: string | null;
  },
  opts?: ProductMutationOpts
) => {
  const organizationId = await requireSessionOrganizationId();
  const normalized = normalizeProductInput(input);
  const isActiveValue = resolveInsertIsActive(input.isActive);

  if (!normalized.sku && normalized.itemType !== 'service') {
    // SKU auto-generado se asigna server-side via generate_next_sku en apply_product_command.
    // Localmente queda vacío hasta que el pull traiga el SKU real desde Supabase.
    normalized.sku = '';
  }

  if (normalized.categoryId) {
    await assertCategoryAllowsItemType(organizationId, normalized.categoryId, normalized.itemType);
  }
  await assertUniqueSku(organizationId, normalized.sku);
  await assertUniqueBarcode(organizationId, normalized.barcode);

  const id = crypto.randomUUID();
  const timestamp = new Date().toISOString();
  const branchId = useSessionStore.getState().currentBranchId;
  const initialQtyRaw = Math.max(0, Number(opts?.initialBranchQuantity ?? 0) || 0);
  const initialQty = normalized.unitOfMeasure === 'unit' ? Math.floor(initialQtyRaw) : initialQtyRaw;

  const productInsertPayload = {
    id,
    organizationId,
    name: normalized.name,
    price: normalized.price,
    sku: normalized.sku,
    barcode: normalized.barcode,
    categoryId: normalized.categoryId,
    updatedAt: timestamp,
    itemType: normalized.itemType,
    isMisc: false,
    isActive: isActiveValue === 1,
    taxClass: normalized.taxClass ?? undefined,
    brandId: normalized.brandId ?? undefined,
    internalNotes: normalized.internalNotes ?? undefined,
    preferredSupplierId: normalized.preferredSupplierId ?? undefined,
    supplierItemCode: normalized.supplierItemCode ?? undefined,
    unitOfMeasure: normalized.unitOfMeasure,
    sellByWeight: normalized.sellByWeight,
    durationMinutes: normalized.durationMinutes ?? undefined,
    bufferBeforeMinutes: normalized.bufferBeforeMinutes,
    bufferAfterMinutes: normalized.bufferAfterMinutes,
    imageUrl: normalized.imageUrl,
    videoUrl: normalized.videoUrl,
    publicDescription: normalized.publicDescription ?? undefined,
    minOrderQty: normalized.minOrderQty ?? undefined,
    maxOrderQty: normalized.maxOrderQty ?? undefined,
    isPublic: normalized.isPublic,
    activeIngredient: normalized.activeIngredient ?? undefined,
    usageTags: normalized.usageTags.length > 0 ? normalized.usageTags : undefined,
    allergens: normalized.allergens.length > 0 ? normalized.allergens : undefined,
    bookingMode: normalized.bookingMode,
    evaluationProductId: normalized.evaluationProductId ?? undefined,
    bookingPaymentMode: normalized.bookingPaymentMode,
    bookingDepositAmount: normalized.bookingDepositAmount ?? undefined,
    bookingDepositPercent: normalized.bookingDepositPercent ?? undefined,
    kitchenDestination: normalized.kitchenDestination,
    prepMinutes: normalized.prepMinutes ?? undefined,
    menuAvailable: normalized.menuAvailable,
    visibleOnMenuCarta: normalized.visibleOnMenuCarta,
    parentProductId: normalized.parentProductId ?? undefined,
  };

  await submitProductCommand('insert', productInsertPayload);
        const { appendWebCatalogProductToCache } = await import('./catalogRamSnapshotCache');
              appendWebCatalogProductToCache(
                {
                  id,
                  name: normalized.name,
                  price: normalized.price,
                  sku: normalized.sku,
                  barcode: normalized.barcode,
                  category_id: normalized.categoryId,
                  is_active: isActiveValue === 1,
                  organization_id: organizationId,
                  item_type: normalized.itemType,
                  is_misc: false,
                  brand_id: normalized.brandId,
                  internal_notes: normalized.internalNotes,
                  preferred_supplier_id: normalized.preferredSupplierId,
                  supplier_item_code: normalized.supplierItemCode,
                  tax_class: normalized.taxClass,
                  unit_of_measure: normalized.unitOfMeasure,
                  sell_by_weight: normalized.sellByWeight,
                  duration_minutes: normalized.durationMinutes,
                  buffer_before_minutes: normalized.bufferBeforeMinutes,
                  buffer_after_minutes: normalized.bufferAfterMinutes,
                  created_at: timestamp,
                  updated_at: timestamp,
                  image_url: normalized.imageUrl,
                  video_url: normalized.videoUrl,
                  public_description: normalized.publicDescription,
                  is_public: normalized.isPublic,
                  active_ingredient: normalized.activeIngredient,
                  usage_tags: normalized.usageTags.length > 0 ? normalized.usageTags : undefined,
                  allergens: normalized.allergens.length > 0 ? normalized.allergens : undefined,
                  booking_mode: normalized.bookingMode,
                  evaluation_product_id: normalized.evaluationProductId,
                  booking_payment_mode: normalized.bookingPaymentMode,
                  booking_deposit_amount: normalized.bookingDepositAmount,
                  booking_deposit_percent: normalized.bookingDepositPercent,
                  kitchen_destination: normalized.kitchenDestination,
                  prep_minutes: normalized.prepMinutes,
                  menu_available: normalized.menuAvailable,
                  visible_on_menu_carta: normalized.visibleOnMenuCarta,
                  min_order_qty: normalized.minOrderQty,
                  max_order_qty: normalized.maxOrderQty,
                },
                normalized.itemType === 'physical' && branchId
                  ? {
                      product_id: id,
                      branch_id: branchId,
                      quantity: initialQty,
                      min_quantity: 0,
                    }
                  : undefined,
              );
        return id;
};

// ─── Update ─────────────────────────────────────────────────────────────────

export const updateProduct = async (
  productId: string,
  input: {
    name: string;
    price: number;
    sku: string;
    categoryId: string | null;
    barcode?: string | null;
    itemType?: 'physical' | 'service';
    brandId?: string | null;
    internalNotes?: string | null;
    preferredSupplierId?: string | null;
    supplierItemCode?: string | null;
    taxClass?: string | null;
    unitOfMeasure?: Product['unitOfMeasure'];
    sellByWeight?: boolean;
    durationMinutes?: number | null;
    bufferBeforeMinutes?: number | null;
    bufferAfterMinutes?: number | null;
    imageUrl?: string | null;
    videoUrl?: string | null;
    publicDescription?: string | null;
    isPublic?: boolean;
    activeIngredient?: string | null;
    usageTags?: string[] | null;
    bookingMode?: 'direct' | 'quote_required';
    evaluationProductId?: string | null;
    bookingPaymentMode?: 'inherit' | 'none' | 'deposit' | 'full';
    bookingDepositAmount?: number | null;
    bookingDepositPercent?: number | null;
    kitchenDestination?: 'kitchen' | 'bar' | 'none';
    menuAvailable?: boolean;
    visibleOnMenuCarta?: boolean;
    parentProductId?: string | null;
  },
  opts?: ProductMutationOpts
): Promise<boolean> => {
  const organizationId = await requireSessionOrganizationId();
  // M1: Detect active variants so the parent price validation can be relaxed.
  const activeVariants = (getCatalogRamSnapshotCache()?.productVariants ?? []).filter(
    (v) => String(v.product_id) === String(productId) && v.is_active !== false,
  );
  const normalized = normalizeProductInput({ ...input, hasActiveVariants: activeVariants.length > 0 });

  const existing = await resolveProductRowForMutation(productId);

  if (!existing) {
    throw new Error('No encontramos el producto para editar.');
  }
  if (existing.organization_id && existing.organization_id !== organizationId) {
    throw new Error('Este producto pertenece a otra organización.');
  }
  if (existing.is_misc === 1) {
    throw new Error('Varios es interno del mostrador. No se edita.');
  }

  const wasPhysical = existing.item_type == null || existing.item_type === 'physical';
  if (wasPhysical && normalized.itemType === 'service') {
    // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
    const stockTotal = sumProductStockQuantityFromWebCache(productId);
    if (stockTotal > 0) {
      throw new Error('No podés convertir en servicio un producto que todavía tiene stock.');
    }
  }

  if (normalized.categoryId) {
    await assertCategoryAllowsItemType(organizationId, normalized.categoryId, normalized.itemType);
  }
  await assertUniqueSku(organizationId, normalized.sku, productId);
  await assertUniqueBarcode(organizationId, normalized.barcode, productId);

  const timestamp = new Date().toISOString();
  const nextBarcode = normalized.barcode ?? existing.barcode ?? null;
  const itemType = normalized.itemType;

  const normName = (value: string) => value.trim().replace(/\s+/g, ' ');
  const priceUnchanged = Math.round(existing.price * 100) === Math.round(normalized.price * 100);
  const rowUnchanged =
    normName(existing.name) === normName(normalized.name) &&
    priceUnchanged &&
    existing.sku === normalized.sku &&
    (existing.category_id ?? null) === (normalized.categoryId ?? null) &&
    (existing.barcode ?? null) === (nextBarcode ?? null) &&
    (existing.item_type === 'service' ? 'service' : 'physical') === itemType &&
    (existing.brand_id ?? null) === (normalized.brandId ?? null) &&
    (existing.internal_notes ?? null) === (normalized.internalNotes ?? null) &&
    (existing.preferred_supplier_id ?? null) === (normalized.preferredSupplierId ?? null) &&
    (existing.supplier_item_code ?? null) === (normalized.supplierItemCode ?? null) &&
    (existing.tax_class ?? null) === (normalized.taxClass ?? null) &&
    (existing.unit_of_measure ?? 'unit') === normalized.unitOfMeasure &&
    (existing.sell_by_weight === 1) === normalized.sellByWeight &&
    (existing.duration_minutes ?? null) === (normalized.durationMinutes ?? null) &&
    (existing.buffer_before_minutes ?? 0) === normalized.bufferBeforeMinutes &&
    (existing.buffer_after_minutes ?? 0) === normalized.bufferAfterMinutes &&
    (existing.image_url ?? null) === (normalized.imageUrl ?? null) &&
    (existing.video_url ?? null) === (normalized.videoUrl ?? null) &&
    (existing.public_description ?? null) === (normalized.publicDescription ?? null) &&
    (existing.is_public === 1) === normalized.isPublic &&
    (existing.active_ingredient ?? null) === (normalized.activeIngredient ?? null) &&
    JSON.stringify(normalized.usageTags) === JSON.stringify(
      Array.isArray(existing.usage_tags) ? existing.usage_tags : [],
    ) &&
    JSON.stringify(normalized.allergens) === JSON.stringify(
      Array.isArray((existing as { allergens?: string[] | null }).allergens)
        ? (existing as { allergens?: string[] | null }).allergens
        : [],
    ) &&
    (existing.booking_mode ?? 'direct') === normalized.bookingMode &&
    (existing.evaluation_product_id ?? null) === (normalized.evaluationProductId ?? null) &&
    (existing.booking_payment_mode ?? 'inherit') === normalized.bookingPaymentMode &&
    (existing.booking_deposit_amount ?? null) === (normalized.bookingDepositAmount ?? null) &&
    (existing.booking_deposit_percent ?? null) === (normalized.bookingDepositPercent ?? null) &&
    (existing.kitchen_destination ?? 'kitchen') === normalized.kitchenDestination &&
    (existing.prep_minutes ?? null) === (normalized.prepMinutes ?? null);

  if (rowUnchanged) {
    return false;
  }

  // ADR-017 Fase 4: cola eliminada — sin UPDATE SQLite local

  await logBusinessAuditEvent('catalog.product.update', {
    productId,
    before: {
      name: existing.name,
      price: existing.price,
      sku: existing.sku,
      categoryId: existing.category_id,
    },
    after: {
      name: normalized.name,
      price: normalized.price,
      sku: normalized.sku,
      categoryId: normalized.categoryId,
    },
  });

  const [beforeCategoryName, afterCategoryName] = await Promise.all([
    resolveCategoryDisplayName(existing.category_id),
    resolveCategoryDisplayName(normalized.categoryId),
  ]);
  const categoryNameForSummary = (resolved: string, categoryId: string | null) => {
    if (resolved === '—') return null;
    if (categoryId && resolved === String(categoryId).trim()) return null;
    return resolved;
  };

  const itemTypeBefore = existing.item_type === 'service' ? 'service' : 'physical';

  await appendProductCatalogEvent({
    productId,
    eventType: 'update',
    summary: buildProductUpdateSummary(
      {
        name: existing.name,
        price: existing.price,
        sku: existing.sku,
        categoryId: existing.category_id,
        categoryName: categoryNameForSummary(beforeCategoryName, existing.category_id),
        barcode: existing.barcode,
        itemType: itemTypeBefore,
        brandName: existing.brand_id,
        supplierName: existing.preferred_supplier_id,
        internalNotes: existing.internal_notes,
        supplierItemCode: existing.supplier_item_code,
        isActive: existing.is_active !== 0,
      },
      {
        name: normalized.name,
        price: normalized.price,
        sku: normalized.sku,
        categoryId: normalized.categoryId,
        categoryName: categoryNameForSummary(afterCategoryName, normalized.categoryId),
        barcode: nextBarcode,
        itemType,
        brandName: normalized.brandId,
        supplierName: normalized.preferredSupplierId,
        internalNotes: normalized.internalNotes,
        supplierItemCode: normalized.supplierItemCode,
        isActive: existing.is_active !== 0,
      }
    ),
    payload: {
      before: {
        name: existing.name,
        price: existing.price,
        sku: existing.sku,
        categoryId: existing.category_id,
      },
      after: {
        name: normalized.name,
        price: normalized.price,
        sku: normalized.sku,
        categoryId: normalized.categoryId,
      },
    },
    performedByUserId: opts?.performedByUserId,
  });

  await submitProductCommand('update', {
    id: productId,
    organizationId,
    name: normalized.name,
    price: normalized.price,
    sku: normalized.sku,
    barcode: nextBarcode,
    categoryId: normalized.categoryId,
    updatedAt: timestamp,
    isActive: existing.is_active !== 0,
    itemType,
    isMisc: existing.is_misc === 1,
    brandId: normalized.brandId ?? undefined,
    internalNotes: normalized.internalNotes ?? undefined,
    preferredSupplierId: normalized.preferredSupplierId ?? undefined,
    supplierItemCode: normalized.supplierItemCode ?? undefined,
    taxClass: normalized.taxClass ?? undefined,
    unitOfMeasure: normalized.unitOfMeasure,
    sellByWeight: normalized.sellByWeight,
    durationMinutes: normalized.durationMinutes ?? undefined,
    bufferBeforeMinutes: normalized.bufferBeforeMinutes,
    bufferAfterMinutes: normalized.bufferAfterMinutes,
    imageUrl: normalized.imageUrl,
    videoUrl: normalized.videoUrl,
    publicDescription: normalized.publicDescription ?? undefined,
    minOrderQty: normalized.minOrderQty ?? undefined,
    maxOrderQty: normalized.maxOrderQty ?? undefined,
    isPublic: normalized.isPublic,
    activeIngredient: normalized.activeIngredient ?? undefined,
    usageTags: normalized.usageTags.length > 0 ? normalized.usageTags : undefined,
    allergens: normalized.allergens.length > 0 ? normalized.allergens : undefined,
    bookingMode: normalized.bookingMode,
    evaluationProductId: normalized.evaluationProductId ?? undefined,
    bookingPaymentMode: normalized.bookingPaymentMode,
    bookingDepositAmount: normalized.bookingDepositAmount ?? undefined,
    bookingDepositPercent: normalized.bookingDepositPercent ?? undefined,
    kitchenDestination: normalized.kitchenDestination,
    prepMinutes: normalized.prepMinutes ?? undefined,
    menuAvailable: normalized.menuAvailable,
    visibleOnMenuCarta: normalized.visibleOnMenuCarta,
    parentProductId: normalized.parentProductId ?? undefined,
  });

  const { patchWebCatalogProductInCache } = await import('./catalogRamSnapshotCache');
        patchWebCatalogProductInCache(productId, {
          name: normalized.name,
          price: normalized.price,
          sku: normalized.sku,
          barcode: nextBarcode,
          category_id: normalized.categoryId,
          item_type: itemType,
          brand_id: normalized.brandId,
          internal_notes: normalized.internalNotes,
          preferred_supplier_id: normalized.preferredSupplierId,
          supplier_item_code: normalized.supplierItemCode,
          tax_class: normalized.taxClass,
          unit_of_measure: normalized.unitOfMeasure,
          sell_by_weight: normalized.sellByWeight,
          duration_minutes: normalized.durationMinutes,
          buffer_before_minutes: normalized.bufferBeforeMinutes,
          buffer_after_minutes: normalized.bufferAfterMinutes,
          image_url: normalized.imageUrl,
          video_url: normalized.videoUrl,
          public_description: normalized.publicDescription,
          is_public: normalized.isPublic,
          active_ingredient: normalized.activeIngredient,
          usage_tags: normalized.usageTags.length > 0 ? normalized.usageTags : undefined,
          allergens: normalized.allergens.length > 0 ? normalized.allergens : undefined,
          booking_mode: normalized.bookingMode,
          evaluation_product_id: normalized.evaluationProductId,
          booking_payment_mode: normalized.bookingPaymentMode,
          booking_deposit_amount: normalized.bookingDepositAmount,
          booking_deposit_percent: normalized.bookingDepositPercent,
          kitchen_destination: normalized.kitchenDestination,
          menu_available: normalized.menuAvailable,
          visible_on_menu_carta: normalized.visibleOnMenuCarta,
          min_order_qty: normalized.minOrderQty,
          max_order_qty: normalized.maxOrderQty,
          updated_at: timestamp,
        });

  return true;
};

// ─── Lifecycle: deactivate / reactivate / bulk ──────────────────────────────

export const deactivateProduct = async (productId: string, opts?: ProductMutationOpts) => {
  const organizationId = await requireSessionOrganizationId();

  const existing = await resolveProductLifecycleRowForMutation(productId);

  if (!existing) {
    throw new Error('No encontramos el producto para desactivar.');
  }
  if (existing.organization_id && existing.organization_id !== organizationId) {
    throw new Error('Este producto pertenece a otra organización.');
  }
  if (existing.is_misc === 1) {
    throw new Error('Varios es interno del mostrador. No se pausa.');
  }

  const timestamp = new Date().toISOString();

  // ADR-017 Fase 4: cola eliminada — sin UPDATE SQLite local

  await logBusinessAuditEvent('catalog.product.deactivate', { productId });

  await appendProductCatalogEvent({
    productId,
    eventType: 'deactivate',
    summary: 'Producto desactivado',
    payload: { productId },
    performedByUserId: opts?.performedByUserId,
  });

  await submitProductCommand('update', {
    id: existing.id,
    organizationId,
    name: existing.name,
    price: existing.price,
    sku: existing.sku,
    barcode: existing.barcode ?? null,
    categoryId: existing.category_id,
    updatedAt: timestamp,
    isActive: false,
    itemType: existing.item_type === 'service' ? 'service' : 'physical',
    isMisc: existing.is_misc === 1,
  });

  const { patchWebCatalogProductInCache } = await import('./catalogRamSnapshotCache');
        patchWebCatalogProductInCache(productId, { is_active: false, updated_at: timestamp });
};

export const reactivateProduct = async (productId: string, opts?: ProductMutationOpts) => {
  const organizationId = await requireSessionOrganizationId();

  const existing = await resolveProductLifecycleRowForMutation(productId);

  if (!existing) {
    throw new Error('No encontramos el producto para reactivar.');
  }
  if (existing.organization_id && existing.organization_id !== organizationId) {
    throw new Error('Este producto pertenece a otra organización.');
  }
  if (existing.is_misc === 1) {
    throw new Error('Varios es interno del mostrador. No se reactiva a mano.');
  }

  const timestamp = new Date().toISOString();

  // ADR-017 Fase 4: cola eliminada — sin UPDATE SQLite local

  await logBusinessAuditEvent('catalog.product.reactivate', { productId });

  await appendProductCatalogEvent({
    productId,
    eventType: 'reactivate',
    summary: 'Producto activado',
    payload: { productId },
    performedByUserId: opts?.performedByUserId,
  });

  await submitProductCommand('update', {
    id: existing.id,
    organizationId,
    name: existing.name,
    price: existing.price,
    sku: existing.sku,
    barcode: existing.barcode ?? null,
    categoryId: existing.category_id,
    updatedAt: timestamp,
    isActive: true,
    itemType: existing.item_type === 'service' ? 'service' : 'physical',
    isMisc: existing.is_misc === 1,
  });

  const { appendWebCatalogProductToCache, getCatalogRamSnapshotCache, patchWebCatalogProductInCache } =
          await import('./catalogRamSnapshotCache');
        const inCache = getCatalogRamSnapshotCache()?.products.some((p) => p.id === productId);
        if (inCache) {
          patchWebCatalogProductInCache(productId, { is_active: true, updated_at: timestamp });
        } else {
          appendWebCatalogProductToCache({
            id: productId,
            name: existing.name,
            price: existing.price,
            sku: existing.sku,
            barcode: existing.barcode,
            category_id: existing.category_id,
            is_active: true,
            organization_id: organizationId,
            item_type: existing.item_type === 'service' ? 'service' : 'physical',
            is_misc: existing.is_misc === 1,
            created_at: timestamp,
            updated_at: timestamp,
          });
        }
};

export const bulkDeactivateProducts = async (productIds: string[], opts?: ProductMutationOpts) => {
  const errors: string[] = [];
  for (const id of productIds) {
    try {
      await deactivateProduct(id, opts);
    } catch (e: any) {
      captureOpsFailure({ module: 'catalog', op: 'catalog.bulk_deactivate', error: e, itemId: id, extra: { productId: id } });
      errors.push(id);
    }
  }
  if (errors.length > 0) {
    throw new Error(`${errors.length} de ${productIds.length} productos no se pudieron ocultar.`);
  }
};

export const bulkReactivateProducts = async (productIds: string[], opts?: ProductMutationOpts) => {
  const errors: string[] = [];
  for (const id of productIds) {
    try {
      await reactivateProduct(id, opts);
    } catch (e: any) {
      captureOpsFailure({ module: 'catalog', op: 'catalog.bulk_reactivate', error: e, itemId: id, extra: { productId: id } });
      errors.push(id);
    }
  }
  if (errors.length > 0) {
    throw new Error(`${errors.length} de ${productIds.length} productos no se pudieron activar.`);
  }
};
